import { useState, useEffect, useCallback } from "react";
import { Loader2, Lock, CreditCard, ShieldCheck, Send, Clock, Ban } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ActionButton, RunButton, ErrorBox, PageRows, errText, fmt, fmtDay, deText } from "./kit";

const STATE_TEXT = {
  keine_freigabe: ["Keine Freigabe", "Für dich besteht keine Freigabe. Du kannst eine zweckgebundene Anfrage an das Mitglied senden."],
  angefragt: ["Anfrage offen", "Deine Anfrage wartet auf die Entscheidung des Mitglieds in der App."],
  abgelaufen: ["Freigabe abgelaufen", "Deine Freigabe ist abgelaufen. Frühere Passwerte werden nicht mehr angezeigt."],
  widerrufen: ["Freigabe widerrufen", "Das Mitglied hat die Freigabe widerrufen. Frühere Passwerte werden nicht mehr angezeigt."],
};
const DAYS = [1, 3, 7, 14, 30].map((d) => ({ value: String(d), label: `${d} ${d === 1 ? "Tag" : "Tage"}` }));

const show = (v) => (Array.isArray(v) ? v.map(deText).join(", ") : typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v) ? fmtDay(v) : deText(v));

function Locked({ member, state, env, reload }) {
  const { user } = useAuth();
  const [title, text] = STATE_TEXT[state.state] || STATE_TEXT.keine_freigabe;
  const ended = ["abgelaufen", "widerrufen"].includes(state.state);
  const fieldOptions = Object.entries(state.field_labels || {}).map(([value, l]) => ({ value, label: l }));
  return (
    <div className="space-y-4" data-testid="access-pass-locked">
      <div className="flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50 p-4">
        {state.state === "angefragt" ? <Clock className="mt-0.5 h-5 w-5 text-orange-600" /> : <Lock className="mt-0.5 h-5 w-5 text-orange-600" />}
        <div>
          <p className="font-semibold text-orange-800" data-testid="access-pass-state">{title}</p>
          <p className="mt-1 text-sm text-orange-800">{text}</p>
          {state.revoked_at ? (
            <p className="mt-1 text-xs text-orange-800" data-testid="access-pass-revoked-at">Vom Mitglied widerrufen am {fmt(state.revoked_at)}</p>
          ) : state.expires_at && (
            <p className="mt-1 text-xs text-orange-800" data-testid="access-pass-expired-at">Letzte Freigabe gültig bis {fmt(state.expires_at)}</p>
          )}
          {state.request_purpose && <p className="mt-1 text-xs text-orange-800">Zweck der Anfrage: {state.request_purpose}</p>}
        </div>
      </div>
      {ended && (
        <div className="space-y-3" data-testid="access-pass-ended">
          <PageRows testId="access-pass-ended" rows={[["Geteilt von", member.name], ["Benannte Empfängerin oder benannter Empfänger", user?.name],
            ["Freigabe", state.state === "widerrufen" ? `Widerrufen${state.revoked_at ? ` am ${fmt(state.revoked_at)}` : ""}` : `Abgelaufen${state.expires_at ? ` am ${fmt(state.expires_at)}` : ""}`],
            ["Frühere Feldwerte", "Nicht verfügbar"]]} />
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {state.state !== "angefragt" && (
          <ActionButton label="Freigabe anfragen" icon={Send} testId="request-consent-button" variant="default" onDone={reload}
            description="Nenne den Zweck und nur die Felder, die du wirklich brauchst. Das Mitglied entscheidet in der App."
            fields={[
              { name: "purpose", label: "Zweck", type: "textarea", required: true },
              { name: "fields", label: "Benötigte Passfelder", type: "checks", options: fieldOptions, required: true },
              { name: "days", label: "Dauer der Freigabe", type: "select", options: DAYS, default: "7", placeholder: false },
            ]}
            onSubmit={async (v) => { await api.post(`/members/${member.id}/access-pass/request`, { ...v, days: Number(v.days) }); return "Anfrage an das Mitglied gesendet."; }} />
        )}
        {state.state === "angefragt" && env && env !== "production" && (
          <RunButton label="Vorschau: Zustimmung simulieren" icon={ShieldCheck} testId="simulate-consent-button" onDone={reload}
            success="Zustimmung simuliert." run={() => api.post(`/members/${member.id}/access-pass/consent`, { granted: true })} />
        )}
      </div>
      {env && env !== "production" && state.state === "angefragt" && (
        <p className="text-xs text-muted-foreground">Nur in der Vorschau: ersetzt die Entscheidung des Mitglieds in der Mitglieder-App.</p>
      )}
    </div>
  );
}

function Granted({ member, data, env, reload }) {
  const labels = data.grant.field_labels || {};
  return (
    <div className="space-y-3" data-testid="access-pass-granted">
      <p className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-800">
        <ShieldCheck className="h-4 w-4" aria-hidden="true" /> Freigabe aktiv bis {fmt(data.grant.expires_at)} · Zweck: {data.grant.purpose}
      </p>
      <dl className="divide-y divide-border rounded-lg border border-border" data-testid="access-pass-data">
        {Object.entries(data.access_pass).map(([k, v]) => (
          <div key={k} className="flex items-center justify-between gap-4 px-4 py-3">
            <dt className="text-xs font-medium text-muted-foreground">{labels[k] || k}</dt>
            <dd className="text-right text-sm font-semibold text-brand-purple">{show(v) || "–"}</dd>
          </div>
        ))}
      </dl>
      <p className="text-xs text-muted-foreground">Nur die freigegebenen Felder werden angezeigt. Die Ansicht wird nicht zwischengespeichert; jeder Zugriff wird protokolliert.</p>
      {env && env !== "production" && (
        <RunButton label="Vorschau: Widerruf simulieren" icon={Ban} testId="simulate-revoke-button" onDone={reload}
          success="Widerruf simuliert." run={() => api.post(`/members/${member.id}/access-pass/consent`, { granted: false })} />
      )}
    </div>
  );
}

export function PassBody({ member, onChange }) {
  const [res, setRes] = useState(null);
  const [env, setEnv] = useState(null);
  const load = useCallback(async () => {
    setRes(null);
    try {
      const { data } = await api.get(`/members/${member.id}/access-pass`);
      setRes({ kind: "data", data });
    } catch (err) {
      if (err.response?.status === 403) setRes({ kind: "locked", state: err.response.data?.detail?.state || { state: "keine_freigabe" } });
      else setRes({ kind: "error", error: errText(err), status: err.response?.status });
    }
  }, [member.id]);
  useEffect(() => {
    load();
    api.get("/").then((r) => setEnv(r.data.env)).catch(() => setEnv(null));
  }, [load]);
  const reload = () => { load(); onChange && onChange(); };
  if (!res) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-brand-magenta" /></div>;
  if (res.kind === "error") return <ErrorBox error={res.error} status={res.status} onRetry={load} />;
  if (res.kind === "locked") return <Locked member={member} state={res.state} env={env} reload={reload} />;
  return <Granted member={member} data={res.data} env={env} reload={reload} />;
}

export function AccessPassDialog({ member, open, onOpenChange, onChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="access-pass-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-brand-purple">
            <CreditCard className="h-5 w-5 text-brand-magenta" aria-hidden="true" /> Access Pass{member ? ` · ${member.name}` : ""}
          </DialogTitle>
          <DialogDescription>Passdaten sind privat. Sichtbar nur mit persönlicher, zweckgebundener und befristeter Freigabe des Mitglieds.</DialogDescription>
        </DialogHeader>
        {open && member && <PassBody member={member} onChange={onChange} />}
      </DialogContent>
    </Dialog>
  );
}
